<template>
    <div class="background-wrapper">

        <v-container max-width="900" class="main-container">
            <!-- ── Skeleton ── -->
            <template v-if="!articlesLoaded">
              <div class="td-skeleton">
                <div class="td-skeleton-hero" />
                <div class="td-skeleton-row" />
                <div class="td-skeleton-row short" />
                <div class="td-skeleton-block" />
              </div>
            </template>
            <div v-else>
                <div v-if="!userStore.isAuthenticated && !leagueSearchQuery" class="auth-prompt text-center pa-6">
                    <v-icon size="56" class="mb-3" color="grey">mdi-account-lock-outline</v-icon>
                    <p class="text-body-1 mb-4">{{ t('auth.loginToSeeWatchedLeagues') }}</p>
                    <v-btn color="primary" @click="router.push('/login')">{{ t('auth.loginButton') }}</v-btn>
                </div>
                <LeagueItem
                    v-else
                    :organizations="allLeagues"
                    :searchQuery="leagueSearchQuery"
                    @watch-league="onWatchLeague"
                    @unwatch-league="onUnwatchLeague"
                    @edit-league="onEditLeague"
                />
            </div>
        </v-container>

        <!-- League Form Modal -->
        <LeagueFormModal v-model="showLeagueFormModal" :leagueId="editingLeagueId" @saved="onLeagueSaved" @deleted="onLeagueDeleted" />

                <!-- Add Event (styled bottom sheet) -->
                <EventFormModal
                    v-model="showAddEventDialog"
                    :initialDate="selectedDate"
                    @saved="() => {}"
                />

        <!-- Bug Report Dialog -->
        <BugReportDialog v-model:show="showBugReportDialog" />

        <!-- Snackbar -->
        <v-snackbar v-model="showSnackbar" location="top" color="black" timeout="3000">
          <v-icon>mdi-content-copy</v-icon> {{ t('nav.invitationCopied') }}
        </v-snackbar>

        <!-- Filter Sheet -->
        <LeagueFilterSheet
          v-model="showFilterSheet"
          :filters="activeFilters"
          @apply="onApplyFilters"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import axiosInstance from '@/services/axiosInstance';
import { useUserStore } from '@/stores/user/userStore';
import { useAppStore } from '@/stores/app/appStore';
import { ILeague } from '@/types';
import LeagueItem from '@/components/Articles/LeagueItems.vue'
import { useQuickActions } from '@/composables/useQuickActions'
import type { QuickActionItem } from '@/stores/quickActions/quickActionsStore'
import { usePermissions } from '@/composables/access.composable'
import { getUserAvatarUrl } from '@/utils/imageUtils'
import { useTheme, useDisplay } from 'vuetify'
import { useNavTransition } from '@/composables/useNavTransition'
import { canAddEvents } from '@/utils/userUtils'
import { useMessageStore } from '@/stores/message/messageStore'
import BugReportDialog from '@/components/BugReportDialog.vue'
import LeagueFormModal from '@/components/league/LeagueFormModal.vue'
import EventFormModal from '@/components/EventFormModal.vue'
import LeagueFilterSheet from '@/components/LeagueFilterSheet.vue'
import type { LeagueFilters } from '@/components/LeagueFilterSheet.vue'

const { canCreateContent } = usePermissions()
const { mobile } = useDisplay()
const { activeTab } = useNavTransition()
const { t } = useI18n({ useScope: 'global' })

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const allLeagues = ref<ILeague[]>([]);
const userStore = useUserStore();
const appStore = useAppStore();
const messageStore = useMessageStore();
const articlesLoaded = ref(false);
const router = useRouter();
const route = useRoute();
const isLeaguesRoute = computed(() => route.path === '/leagues')
const theme = useTheme();
const isDarkMode = computed(() => theme.global.current.value.dark);

const showAddEventDialog = ref(false);
const showBugReportDialog = ref(false);
const selectedDate = ref<Date | null>(null);
const canAddEventAllowed = ref(false);
const showSnackbar = ref(false);
const showLeagueFormModal = ref(false);
const editingLeagueId = ref<string | null>(null);

// Search state
const leagueSearchQuery = ref('');

// Filter state
const showFilterSheet = ref(false);
const activeFilters = ref<LeagueFilters>({
  city: '',
  ageGroup: [],
});

const hasActiveFilters = computed(() => {
  const f = activeFilters.value;
  return !!(f.city || f.ageGroup.length);
});

onMounted(async () => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});

// Split leagues by type
const leaguesList = computed(() => allLeagues.value.filter(l => l.type !== 'academy'));
const academiesList = computed(() => allLeagues.value.filter(l => l.type === 'academy'));

// Inline search handlers
function onLeagueSearchInline(q: string) {
  leagueSearchQuery.value = q;
}

function onLeagueSearchClose() {
  leagueSearchQuery.value = '';
}

// Kontrola, zda přihlášený uživatel má roli coach nebo administrator
const canEditContent = computed(() => {
    const roles = userStore.user?.userRoles || [];
    return roles.includes('administrator');
});

// Avatar
const userAvatar = computed(() => {
    if (!userStore.user) return null;
    return getUserAvatarUrl(userStore.user);
});
const userInitials = computed(() => {
    if (!userStore.user) return '';
    const names = userStore.user.name?.split(' ') || [];
    return names.map((n: string) => n[0]).join('').toUpperCase();
});

// Quick action items
const quickActionItems = computed<QuickActionItem[]>(() => {
    const items: QuickActionItem[] = [];

    if (userStore.isAuthenticated) {
        items.push({ icon: 'mdi-send', label: `${t('nav.messages')}${messageStore.unreadCount > 0 ? ` (${messageStore.unreadCount})` : ''}`, action: 'messages' });
        items.push({ icon: 'mdi-badge-account-outline', label: t('myProfile'), action: 'profile' });
        items.push({ icon: 'mdi-account-group', label: t('nav.myPlayers'), action: 'players' });
        items.push({ icon: 'mdi-account-plus', label: t('nav.createInvitation'), action: 'createInvitation' });
        items.push({ icon: 'mdi-cog-outline', label: t('nav.accountSettings'), action: 'account' });
        items.push({ icon: 'mdi-logout', label: t('nav.logout'), action: 'logout' });
        items.push({ icon: 'mdi-bug', label: t('nav.reportBug'), action: 'bugReport' });
        items.push({ icon: isDarkMode.value ? 'mdi-weather-sunny' : 'mdi-weather-night', label: isDarkMode.value ? t('nav.lightMode') : t('nav.darkMode'), action: 'toggleTheme' });
    }

    return items;
});

async function createInvitation() {
    try {
        const url = `${window.location.origin}/register?referent=${userStore.getUserId}`;
        await navigator.clipboard.writeText(url);
        showSnackbar.value = true;
    } catch (e) {
        console.error('Nepodařilo se zkopírovat pozvánku:', e);
    }
}

function openMyProfile() {
    if (!userStore.user?._id || !userStore.user?.searchName) return;
    const parts = userStore.user.searchName.split(' ');
    if (parts.length < 2) return;
    router.push(`/users/${userStore.user._id}-${parts[0]}-${parts[1]}`);
}

function onQuickAction(action: string) {
    switch (action) {
        case 'createLeague':
            openCreateLeague();
            break;
        case 'joinTeam':
            // TODO: implement join team flow
            break;
        case 'messages':
            router.push('/messages');
            break;
        case 'profile':
            openMyProfile();
            break;
        case 'players':
            router.push('/players');
            break;
        case 'logout':
            localStorage.removeItem('token');
            userStore.clearUser();
            window.location.href = '/login';
            break;
        case 'createEvent':
            selectedDate.value = new Date();
            showAddEventDialog.value = true;
            break;
        case 'createInvitation':
            createInvitation();
            break;
        case 'account':
            router.push('/account');
            break;
        case 'bugReport':
            showBugReportDialog.value = true;
            break;
        case 'toggleTheme':
            theme.global.name.value = isDarkMode.value ? 'light' : 'customDarkTheme';
            localStorage.setItem('theme', theme.global.name.value);
            break;
    }
}

const qaVisible = computed(() => (!mobile.value || activeTab.value === 2) && isLeaguesRoute.value && canCreateContent.value)

const qa = useQuickActions({
    visible: qaVisible.value,
    primaryIcon: 'mdi-plus',
    items: quickActionItems.value,
    avatarSrc: userAvatar.value,
    avatarInitials: userInitials.value,
    showFilter: true,
    filterActive: hasActiveFilters.value,
    showSearch: true,
    searchPlaceholder: t('nav.searchOrganization'),
    onPrimary: openCreateLeague,
    onAction: onQuickAction,
    onFilter: () => { showFilterSheet.value = true },
    onSearch: onLeagueSearchInline,
    onSearchClose: onLeagueSearchClose,
})
qa.sync(() => qaVisible.value, 'visible')
qa.sync(() => quickActionItems.value, 'items')
qa.sync(() => hasActiveFilters.value, 'filterActive')
qa.sync(() => userAvatar.value, 'avatarSrc')
qa.sync(() => userInitials.value, 'avatarInitials')
qa.reclaimWhen(() => isLeaguesRoute.value && activeTab.value === 2)

function openCreateLeague() {
    editingLeagueId.value = null;
    showLeagueFormModal.value = true;
}

function onEditLeague(league: ILeague) {
    editingLeagueId.value = league.urlTitle || league._id;
    showLeagueFormModal.value = true;
}

const onLeagueSaved = async () => {
    const articles = await fetchArticles();
    allLeagues.value = articles;
};

function getLeagueId(league: any) {
    return league.id ?? league._id ?? league.urlTitle;
}

// Funkce pro načtení lig
const fetchArticles = async () => {
    // Nepřihlášený uživatel bez vyhledávacího dotazu nic nevidí
    if (!userStore.isAuthenticated && !leagueSearchQuery.value) {
        return [];
    }
    try {
        const all = canEditContent.value ? true : false;
        const params: Record<string, any> = { limit: 100, all };

        // Search query
        if (leagueSearchQuery.value) params.q = leagueSearchQuery.value;

        // Filters
        if (activeFilters.value.city) params.city = activeFilters.value.city;
        if (activeFilters.value.ageGroup.length) params.ageGroup = activeFilters.value.ageGroup.join(',');

        const response = await axiosInstance.get<ILeague[]>(`${API_BASE_URL}/leagues`, { params });
        return response.data;
    } catch (error) {
        console.error('Chyba při načítání lig:', error);
        return [];
    }
};

// Apply filters
async function onApplyFilters(filters: LeagueFilters) {
  activeFilters.value = { ...filters };
  const articles = await fetchArticles();
  allLeagues.value = articles;
}

// Refetch when search query changes
watch(leagueSearchQuery, async () => {
  const articles = await fetchArticles();
  allLeagues.value = articles;
});

// Jednotné volání watch/unwatch
async function sendWatch(league: ILeague, value: boolean) {
    try {
        const id = getLeagueId(league);
        await axiosInstance.post(`${API_BASE_URL}/users/watch`, {
            type: 'league',
            id,
            value,
        });
        if (value) {
            if (!userStore.user?.watchedLeagues) userStore.user!.watchedLeagues = [];
            if (!userStore.user!.watchedLeagues.includes(id)) {
                userStore.user!.watchedLeagues.push(id);
            }
        } else {
            if (userStore.user?.watchedLeagues) {
                userStore.user!.watchedLeagues = userStore.user!.watchedLeagues.filter((lid: string) => lid !== id);
            }
        }
        appStore.triggerRefetch();
    } catch (err) {
        console.error('Nepodařilo se změnit sledování ligy:', err);
    }
}

function onWatchLeague(league: ILeague) {
    return sendWatch(league, true);
}

function onUnwatchLeague(league: ILeague) {
    return sendWatch(league, false);
}

function onLeagueDeleted(leagueId: string) {
    allLeagues.value = allLeagues.value.filter(l => String(l._id) !== leagueId);
}

// Načtení dat při mountování komponenty
onMounted(async () => {
    const articles = await fetchArticles();
    allLeagues.value = articles;
    articlesLoaded.value = true;
});
</script>

<style scoped>
/* ===== BACKGROUND ===== */
.background-wrapper {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
}

.auth-prompt {
  margin-top: 32px;
}

/* ── Skeleton ── */
.td-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 40px;
}
.td-skeleton-hero {
  height: 120px;
  border-radius: 20px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  animation: pulse 1.4s ease-in-out infinite;
}
.td-skeleton-row {
  height: 48px;
  border-radius: 14px;
  background: rgba(var(--v-theme-sheetText), 0.06);
  animation: pulse 1.4s ease-in-out infinite;
  &.short { width: 60%; }
}
.td-skeleton-block {
  height: 200px;
  border-radius: 20px;
  background: rgba(var(--v-theme-sheetText), 0.06);
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
